import { faArrowRight, faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ErrorMessage from "components/reusable/ErrorMessage";
import Toolbar from "components/Toolbar";
import AuthContext from "context/AuthContext";
import useToastContext from "hooks/useToastContext";
import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { apiPut } from "utils/apiFetch";
import pathObj from "utils/apiPath";
import { validationRegex } from "utils/constants";
import EditEmail from "./EditEmail";
import EditPhoneNumber from "./EditPhoneNumber";

const EditProfile = () => {
  const { t } = useTranslation();
  const { user, getProfile, storeTokens } = useContext(AuthContext);

  const notification = useToastContext();
  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({ mode: "onChange", shouldFocusError: true, defaultValues: {} });

  const [showEditPhoneNumberBottomSheet, setShowEditPhoneNumberBottomSheet] = useState(false);
  const [showEditEmailBottomSheet, setShowEditEmailBottomSheet] = useState(false);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setValue("first_name", user.first_name);
    setValue("last_name", user.last_name);
    setValue("businessName", user.businessName);
    setValue("address", user.address);
    setValue("city", user.city);
    setValue("zip_code", user.zip_code);
  }, [user, setValue]);

  const onSubmitMain = async (data) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("first_name", data.first_name);
      formData.append("last_name", data.last_name);
      formData.append("businessName", data.businessName);
      formData.append("address", data.address);
      formData.append("city", data.city);
      formData.append("zip_code", data.zip_code);

      let config = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };
      const res = await apiPut(pathObj.updateProfile, formData, config);
      if (res.data.success) {
        storeTokens(res.data.results.token, res.data.results.refresh_token);
        notification.success(res.data.message);
      } else {
        notification.error(res.data.message);
      }
    } catch (err) {}
    setLoading(false);
  };

  const preventSpace = (e) => {
    e.target.value = e.target.value.trim();
  };

  return (
    <>
      <Toolbar title={t("PERSONAL_DETAILS")} showBackBtn={true} />
      <form onSubmit={handleSubmit(onSubmitMain)} className="flex flex-col w-full justify-center items-center">
        <div className="mt-3 p-4 rounded-3xl h-full w-full md:w-[25rem]">
          <div className="mt-2 mx-2 w-full flex flex-col justify-center items-center">
            <div className="grid grid-cols-2 gap-2 mt-5 w-[90%] md:w-[25rem]">
              <div className="col-span-2 md:col-span-1">
                <input
                  type="text"
                  id="first_name"
                  className="inputform text-heading text-sm rounded-base block w-full px-3 py-2.5 shadow-xs placeholder:text-body"
                  placeholder={t("O_FIRST_NAME")}
                  onKeyDown={(e) => preventSpace(e)}
                  {...register("first_name", {
                    required: t("REGISTER_FIRST_NAME"),
                    minLength: {
                      value: 2,
                      message: t("REGISTER_MIN_LENGTH"),
                    },
                    validate: (value) => {
                      return !!value.trim() ? true : t("REGISTER_WHITE_SPACE");
                    },
                  })}
                  required
                />
                <ErrorMessage message={errors?.first_name?.message} />
              </div>
              <div className="col-span-2 md:col-span-1">
                <input
                  type="text"
                  id="last_name"
                  className="inputform text-heading text-sm rounded-base block w-full px-3 py-2.5 shadow-xs placeholder:text-body"
                  placeholder={t("O_LAST_NAME")}
                  onKeyDown={(e) => preventSpace(e)}
                  {...register("last_name", {
                    required: t("REGISTER_LAST_NAME"),
                    minLength: {
                      value: 2,
                      message: t("REGISTER_MIN_LENGTH"),
                    },
                    validate: (value) => {
                      return !!value.trim() ? true : t("REGISTER_WHITE_SPACE");
                    },
                  })}
                  required
                />
                <ErrorMessage message={errors?.last_name?.message} />
              </div>
              <div className="col-span-2 flex gap-2">
                <div className="w-[6rem]">
                  <input
                    type="text"
                    id="country_code"
                    className="text-gray-600 disabled inputform text-heading text-sm rounded-base block w-full px-3 py-2.5 shadow-xs placeholder:text-body"
                    placeholder={t("O_LAST_NAME")}
                    disabled
                    value={`+${user.country_code}`}
                    onKeyDown={(e) => preventSpace(e)}
                    required
                  />
                </div>
                <div className="w-full">
                  <input
                    type="number"
                    id="mobile"
                    inputType="numeric"
                    maxLength={9}
                    disabled
                    value={user.mobile}
                    className="inputform text-heading disabled text-gray-600 text-sm rounded-base block w-full px-3 py-2.5 shadow-xs placeholder:text-body"
                    placeholder={t("O_MOBILE_NUMBER")}
                    required
                  />
                  <ErrorMessage message={errors?.phone?.message} />
                </div>
              </div>
              <div
                className="text-primary col-span-2 flex w-full justify-end items-center gap-2 cursor-pointer"
                onClick={() => {
                  setShowEditPhoneNumberBottomSheet(true);
                }}
              >
                <span>{t("O_ADD")}</span>
                <FontAwesomeIcon icon={faArrowRight} className="text-[var(--primary)]" />
              </div>

              <div className="col-span-2">
                <input
                  type="text"
                  id="email"
                  disabled
                  value={user.email}
                  className="inputform text-heading disabled text-gray-600 text-sm rounded-base block w-full px-3 py-2.5 shadow-xs placeholder:text-body"
                  placeholder={t("O_EMAIL")}
                  onKeyDown={(e) => preventSpace(e)}
                  {...register("email", {
                    required: t("REGISTER_EMAIL"),
                    pattern: {
                      value: validationRegex.email,
                      message: t("REGISTER_EMAIL_EX"),
                    },
                  })}
                  required
                />
                <ErrorMessage message={errors?.email?.message} />
              </div>

              <div
                className="text-primary col-span-2 flex w-full justify-end items-center gap-2 cursor-pointer"
                onClick={() => {
                  setShowEditEmailBottomSheet(true);
                }}
              >
                <span>{t("O_EDIT")}</span>
                <FontAwesomeIcon icon={faArrowRight} className="text-[var(--primary)]" />
              </div>

              <div className="col-span-2 flex gap-2">
                <div className="w-full">
                  <input
                    type="text"
                    id="city"
                    className="inputform text-heading text-sm rounded-base block w-full px-3 py-2.5 shadow-xs placeholder:text-body"
                    placeholder={t("O_CITY")}
                    onKeyDown={(e) => preventSpace(e)}
                    {...register("city", {
                      required: t("REGISTER_CITY"),
                    })}
                  />
                </div>
                <div className="w-[8rem]">
                  <input
                    type="number"
                    inputMode="numeric"
                    id="zip_code"
                    className="inputform text-heading text-sm rounded-base block w-full px-3 py-2.5 shadow-xs placeholder:text-body"
                    placeholder={t("O_ZIP_CODE")}
                    onKeyDown={(e) => preventSpace(e)}
                    {...register("zip_code", {
                      required: t("REGISTER_ZIP_CODE"),
                    })}
                  />
                </div>
              </div>

              <div className="col-span-2">
                <input
                  type="text"
                  id="address"
                  className="inputform text-heading text-sm rounded-base block w-full px-3 py-2.5 shadow-xs placeholder:text-body"
                  placeholder={t("ADDRESS")}
                  {...register("address", {
                    required: t("REGISTER_ADDRESS"),
                  })}
                />
              </div>

              <div className="col-span-2 pb-4">
                <div className="w-full mt-5">
                  {loading ? (
                    <div className="btn btn_theme w-full flex items-center justify-center p-3">
                      <FontAwesomeIcon icon={faCircleNotch} className="text-white text-xl animate-spin" />
                    </div>
                  ) : (
                    <button className="btn btn_theme w-full text-white" type="submit">
                      {t("SAVE")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
      {showEditPhoneNumberBottomSheet && <EditPhoneNumber onClose={() => setShowEditPhoneNumberBottomSheet(false)} />}
      {showEditEmailBottomSheet && <EditEmail onClose={() => setShowEditEmailBottomSheet(false)} />}
    </>
  );
};

export default EditProfile;
